import React, { useState, useEffect } from 'react';
import Chart from '../../../../Assets/Images/spider-chart.png';
import SpiderChart from './SpiderChart';
import LeadershipChart from './LeadershipChart';
import HeaderPreview from "../../../GeneratePdf/HeaderPreview";
import FooterPreview from "../../../GeneratePdf/FooterPreview";
import { applyFullTieOrder } from '../../../../utils/quadrantTieOrder';

const AlignmentFlowSecond = ({ apiData, pageNumber }) => {
  const [snapshotResult, setSnapshotResults] = useState([]);
  const [starterLabel, setStarterLabel] = useState([]);
  const [variablsMapArray, setVariableMapArray] = useState();
  // Leadership chart data
  const [leadershipResult, setLeadershipResult] = useState([]);
  const [leadershipLabel, setLeadershipLabel] = useState([]);

  const [scoreRanking, setScoreRanking] = useState([])
  const [scoreRankingOne, setScoreRankingOne] = useState([])



  const variableMap = apiData?.variables?.reduce((acc, el) => {
    acc[el.variable] = el.score;
    return acc;
  }, {});

  useEffect(() => {
    if (variableMap) {
      setVariableMapArray(variableMap);
    }
  }, [apiData]);

  useEffect(() => {
    if (variablsMapArray) {
      // --- HEART/SOUL/MIND/STRENGTH CALCULATIONS (WHO YOU ARE) ---
      const heart_r1_m = variablsMapArray["heart_round1_m"] || 0;
      const soul_r1_m = variablsMapArray["soul_round1_m"] || 0;
      const mind_r1_m = variablsMapArray["mind_round1_m"] || 0;
      const strength_r1_m = variablsMapArray["str_round1_m"] || 0;

      const heart_r2_m = variablsMapArray["heart_round2_m"] || 0;
      const soul_r2_m = variablsMapArray["soul_round2_m"] || 0;
      const mind_r2_m = variablsMapArray["mind_round2_m"] || 0;
      const strength_r2_m = variablsMapArray["str_round2_m"] || 0;

      const heart_r1_l = variablsMapArray["heart_round1_l"] || 0;
      const soul_r1_l = variablsMapArray["soul_round1_l"] || 0;
      const mind_r1_l = variablsMapArray["mind_round1_l"] || 0;
      const strength_r1_l = variablsMapArray["str_round1_l"] || 0;

      const heart_r2_l = variablsMapArray["heart_round2_l"] || 0;
      const soul_r2_l = variablsMapArray["soul_round2_l"] || 0;
      const mind_r2_l = variablsMapArray["mind_round2_l"] || 0;
      const strength_r2_l = variablsMapArray["str_round2_l"] || 0;

      // Calculate highest scores for each quadrant
      const heart_highest = Math.max(heart_r1_m, heart_r2_m, heart_r1_l, heart_r2_l);
      const mind_highest = Math.max(mind_r1_m, mind_r2_m, mind_r1_l, mind_r2_l);
      const soul_highest = Math.max(soul_r1_m, soul_r2_m, soul_r1_l, soul_r2_l);
      const strength_highest = Math.max(strength_r1_m, strength_r2_m, strength_r1_l, strength_r2_l);

      // Calculate most, second most, least, second least scores
      let most_score = Math.max(heart_r1_m, soul_r1_m, mind_r1_m, strength_r1_m);
      let second_most_score = Math.max(heart_r2_m, soul_r2_m, mind_r2_m, strength_r2_m) / 2;
      let least_score = Math.max(heart_r1_l, soul_r1_l, mind_r1_l, strength_r1_l);
      let second_least_score = Math.max(heart_r2_l, soul_r2_l, mind_r2_l, strength_r2_l) / 2;

      if (least_score >= second_least_score) {
        least_score = (second_least_score - 1);
      }

      const total = most_score + second_most_score + least_score + second_least_score;

      const most_per = (most_score / total) * 100;
      const second_most_per = (second_most_score / total) * 100;
      const least_per = (least_score / total) * 100;
      const second_least_per = (second_least_score / total) * 100;

      const labels_arr = ["heart", "soul", "mind", "strength"];

      const mostValues = [heart_r1_m, soul_r1_m, mind_r1_m, strength_r1_m];
      const secondMostValues = [heart_r2_m, soul_r2_m, mind_r2_m, strength_r2_m];
      const leastValues = [heart_r1_l, soul_r1_l, mind_r1_l, strength_r1_l];
      const secondLeastValues = [heart_r2_l, soul_r2_l, mind_r2_l, strength_r2_l];

      const most_score_label = Math.max(...mostValues);
      const second_most_score_label = Math.max(...secondMostValues);
      const least_score_label = Math.max(...leastValues);
      const second_least_score_label = Math.max(...secondLeastValues);

      const getLabel = (values, maxValue) => labels_arr[values.indexOf(maxValue)];

      const most_label = getLabel(mostValues, most_score_label);
      const second_most_label = getLabel(secondMostValues, second_most_score_label);
      const least_label = getLabel(leastValues, least_score_label);
      const second_least_label = getLabel(secondLeastValues, second_least_score_label);

      const getPercentageOrder = (label) => {
        if (label === most_label?.toLowerCase()) return most_per;
        if (label === second_most_label?.toLowerCase()) return second_most_per;
        if (label === least_label?.toLowerCase()) return least_per;
        if (label === second_least_label?.toLowerCase()) return second_least_per;
        return 0;
      };

      // --- COLLABORATIVE/VISIONARY/PERFORMANCE/STRATEGIC CALCULATIONS (HOW YOU LEAD) ---
      const performance_r1_m = variablsMapArray["per_round1_m"] || 0;
      const strategic_r1_m = variablsMapArray["strat_round1_m"] || 0;
      const visionary_r1_m = variablsMapArray["vis_round1_m"] || 0;
      const collaborative_r1_m = variablsMapArray["col_round1_m"] || 0;

      const performance_r2_m = variablsMapArray["per_round2_m"] || 0;
      const strategic_r2_m = variablsMapArray["strat_round2_m"] || 0;
      const visionary_r2_m = variablsMapArray["vis_round2_m"] || 0;
      const collaborative_r2_m = variablsMapArray["col_round2_m"] || 0;

      const performance_r1_l = variablsMapArray["per_round1_l"] || 0;
      const strategic_r1_l = variablsMapArray["strat_round1_l"] || 0;
      const visionary_r1_l = variablsMapArray["vis_round1_l"] || 0;
      const collaborative_r1_l = variablsMapArray["col_round1_l"] || 0;

      const performance_r2_l = variablsMapArray["per_round2_l"] || 0;
      const strategic_r2_l = variablsMapArray["strat_round2_l"] || 0;
      const visionary_r2_l = variablsMapArray["vis_round2_l"] || 0;
      const collaborative_r2_l = variablsMapArray["col_round2_l"] || 0;

      // Calculate most, second most, least, second least scores for leadership
      let leadership_most_score = Math.max(performance_r1_m, strategic_r1_m, visionary_r1_m, collaborative_r1_m);
      let leadership_second_most_score = Math.max(performance_r2_m, strategic_r2_m, visionary_r2_m, collaborative_r2_m) / 2;
      let leadership_least_score = Math.max(performance_r1_l, strategic_r1_l, visionary_r1_l, collaborative_r1_l);
      let leadership_second_least_score = Math.max(performance_r2_l, strategic_r2_l, visionary_r2_l, collaborative_r2_l) / 2;

      if (leadership_least_score >= leadership_second_least_score) {
        leadership_least_score = leadership_second_least_score - 1;
      }

      const leadership_total = leadership_most_score + leadership_second_most_score + leadership_least_score + leadership_second_least_score;

      const leadership_most_per = (leadership_most_score / leadership_total) * 100;
      const leadership_second_most_per = (leadership_second_most_score / leadership_total) * 100;
      const leadership_least_per = (leadership_least_score / leadership_total) * 100;
      const leadership_second_least_per = (leadership_second_least_score / leadership_total) * 100;

      const leadership_labels_arr = ["performance", "strategic", "visionary", "collaborative"];

      const leadership_mostValues = [performance_r1_m, strategic_r1_m, visionary_r1_m, collaborative_r1_m];
      const leadership_secondMostValues = [performance_r2_m, strategic_r2_m, visionary_r2_m, collaborative_r2_m];
      const leadership_leastValues = [performance_r1_l, strategic_r1_l, visionary_r1_l, collaborative_r1_l];
      const leadership_secondLeastValues = [performance_r2_l, strategic_r2_l, visionary_r2_l, collaborative_r2_l];

      const leadership_most_score_label = Math.max(...leadership_mostValues);
      const leadership_second_most_score_label = Math.max(...leadership_secondMostValues);
      const leadership_least_score_label = Math.max(...leadership_leastValues);
      const leadership_second_least_score_label = Math.max(...leadership_secondLeastValues);

      const getLeadershipLabel = (values, maxValue) => leadership_labels_arr[values.indexOf(maxValue)];

      const leadership_most_label = getLeadershipLabel(leadership_mostValues, leadership_most_score_label);
      const leadership_second_most_label = getLeadershipLabel(leadership_secondMostValues, leadership_second_most_score_label);
      const leadership_least_label = getLeadershipLabel(leadership_leastValues, leadership_least_score_label);
      const leadership_second_least_label = getLeadershipLabel(leadership_secondLeastValues, leadership_second_least_score_label);

      const getLeadershipPercentageOrder = (label) => {
        if (label === leadership_most_label?.toLowerCase()) return leadership_most_per;
        if (label === leadership_second_most_label?.toLowerCase()) return leadership_second_most_per;
        if (label === leadership_least_label?.toLowerCase()) return leadership_least_per;
        if (label === leadership_second_least_label?.toLowerCase()) return leadership_second_least_per;
        return 0;
      };

      // Calculate all values individually
      const heart = getPercentageOrder("heart");
      const strength = getPercentageOrder("strength");
      const soul = getPercentageOrder("soul");
      const mind = getPercentageOrder("mind");

      const RankedScoresOne = [
        { name: "heart", value: parseFloat(heart.toFixed(2)) },
        { name: "strength", value: parseFloat(strength.toFixed(2)) },
        { name: "soul", value: parseFloat(soul.toFixed(2)) },
        { name: "mind", value: parseFloat(mind.toFixed(2)) }
      ];


      setScoreRankingOne(RankedScoresOne)

      const collaborative = getLeadershipPercentageOrder("collaborative");
      const performance = getLeadershipPercentageOrder("performance");
      const visionary = getLeadershipPercentageOrder("visionary");
      const strategic = getLeadershipPercentageOrder("strategic");


      const RankedScores = [
        { name: "Collaborative", value: parseFloat(collaborative.toFixed(2)) },
        { name: "Strategic", value: parseFloat(strategic.toFixed(2)) },
        { name: "Performance", value: parseFloat(performance.toFixed(2)) },
        { name: "Visionary", value: parseFloat(visionary.toFixed(2)) }
      ];

      setScoreRanking(RankedScores);

      // For AlignmentFlow (radar) chart
      const lifeRadarData = [heart, strength, soul, mind];
      const lifeRadarLabels = ['HEART', 'STRENGTH', 'SOUL', 'MIND'];

      const leadershipRadarData = [collaborative, performance, visionary, strategic];
      const leadershipRadarLabels = ['COLLABORATIVE', 'PERFORMANCE', 'VISIONARY', 'STRATEGIC'];

      // Pass these to SpiderChart
      setSnapshotResults(lifeRadarData);
      setStarterLabel(lifeRadarLabels);
      setLeadershipResult(leadershipRadarData);
      setLeadershipLabel(leadershipRadarLabels);
    }
  }, [variablsMapArray]);

  const SquareWithArrowTwo = () => {

    const leadershipScores = {
      strategic: scoreRanking.find(item => item.name.toLowerCase() === 'strategic')?.value || 0,
      performance: scoreRanking.find(item => item.name.toLowerCase() === 'performance')?.value || 0,
      visionary: scoreRanking.find(item => item.name.toLowerCase() === 'visionary')?.value || 0,
      collaborative: scoreRanking.find(item => item.name.toLowerCase() === 'collaborative')?.value || 0
    };

    const sortedLeadership = applyFullTieOrder(
      Object.entries(leadershipScores).sort((a, b) => b[1] - a[1]),
      ([name]) => name,
      ([, value]) => value
    )
      .map(([name, value]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value: Math.round(value)
      }));

    const diagramSize = 206;
    const headLength = 14;
    const headWidth = 8;
    const centerOffsetX = diagramSize / 2;
    const centerOffsetY = diagramSize / 2;
    const quadrantOffsetX = centerOffsetX / 2;
    const quadrantOffsetY = centerOffsetY / 2;

    const quadrantCenters = {
      strategic: { x: quadrantOffsetX, y: quadrantOffsetY },
      performance: { x: diagramSize - quadrantOffsetX, y: quadrantOffsetY },
      visionary: { x: quadrantOffsetX, y: diagramSize - quadrantOffsetY },
      collaborative: { x: diagramSize - quadrantOffsetX, y: diagramSize - quadrantOffsetY }
    };

    const pathPoints = sortedLeadership.map(step => quadrantCenters[step.name.toLowerCase()]);

    let arrowPath = '';
    if (pathPoints.length > 0) {
      const pointsForPath = [...pathPoints];
      if (pointsForPath.length > 1) {
        const tip = pointsForPath[pointsForPath.length - 1];
        const prev = pointsForPath[pointsForPath.length - 2];
        const dx = tip.x - prev.x;
        const dy = tip.y - prev.y;
        const len = Math.hypot(dx, dy) || 1;
        const trimmedTip = {
          x: tip.x - (headLength * dx) / len,
          y: tip.y - (headLength * dy) / len
        };
        pointsForPath[pointsForPath.length - 1] = trimmedTip;
      }

      arrowPath = `M ${pointsForPath[0].x} ${pointsForPath[0].y}`;
      for (let i = 1; i < pointsForPath.length; i++) {
        arrowPath += ` L ${pointsForPath[i].x} ${pointsForPath[i].y}`;
      }
    }

    const dotPosition = sortedLeadership[0] ?
      quadrantCenters[sortedLeadership[0].name.toLowerCase()] :
      { x: 0, y: 0 };

    const arrowColor = '#B6111F';
    let arrowHeadPoints = '';



    if (sortedLeadership.length > 1) {
      const lastIdx = sortedLeadership.length - 1;
      const tip = quadrantCenters[sortedLeadership[lastIdx].name.toLowerCase()];
      const prev = quadrantCenters[sortedLeadership[lastIdx - 1].name.toLowerCase()];
      const angle = Math.atan2(tip.y - prev.y, tip.x - prev.x);
      const left = {
        x: tip.x - headLength * Math.cos(angle) + headWidth * Math.sin(angle),
        y: tip.y - headLength * Math.sin(angle) - headWidth * Math.cos(angle)
      };
      const right = {
        x: tip.x - headLength * Math.cos(angle) - headWidth * Math.sin(angle),
        y: tip.y - headLength * Math.sin(angle) + headWidth * Math.cos(angle)
      };
      arrowHeadPoints = `${tip.x},${tip.y} ${left.x},${left.y} ${right.x},${right.y}`;
    }

    return (
      <div style={{ position: 'relative', width: `${diagramSize}px`, height: `${diagramSize}px`, backgroundColor: 'transparent', display: 'flex', justifyContent: 'center', alignItems: 'center', border: '2px solid #000000' }}>
        <div style={{ position: 'absolute', width: '2px', height: '100%', backgroundColor: '#000', left: '50%', transform: 'translateX(-50%)' }}></div>
        <div style={{ position: 'absolute', width: '100%', height: '2px', backgroundColor: '#000', top: '50%', transform: 'translateY(-50%)' }}></div>

        <div style={{ position: 'absolute', top: '5px', left: '10px', color: '#000', fontFamily: 'Helvetica', fontWeight: 700, fontStyle: 'normal', fontSize: '10px', lineHeight: '14px', letterSpacing: '0px', leadingTrim: 'none' }}>STRATEGIC</div>
        <div style={{ position: 'absolute', top: '20px', left: '10px', color: '#000', fontFamily: 'Helvetica', fontWeight: 700, fontStyle: 'normal', fontSize: '10px', lineHeight: '14px', letterSpacing: '0px', leadingTrim: 'none' }}>
          {typeof leadershipScores.strategic === 'number' ? leadershipScores.strategic.toFixed(2) : '0.00'}%
        </div>

        <div style={{ position: 'absolute', top: '5px', right: '10px', color: '#000', fontFamily: 'Helvetica', fontWeight: 700, fontStyle: 'normal', fontSize: '10px', lineHeight: '14px', letterSpacing: '0px', leadingTrim: 'none' }}>PERFORMANCE</div>
        <div style={{ position: 'absolute', top: '20px', right: '10px', color: '#000', fontFamily: 'Helvetica', fontWeight: 700, fontStyle: 'normal', fontSize: '10px', lineHeight: '14px', letterSpacing: '0px', leadingTrim: 'none' }}>
          {typeof leadershipScores.performance === 'number' ? leadershipScores.performance.toFixed(2) : '0.00'}%
        </div>

        <div style={{ position: 'absolute', bottom: '5px', left: '10px', color: '#000', fontFamily: 'Helvetica', fontWeight: 700, fontStyle: 'normal', fontSize: '10px', lineHeight: '14px', letterSpacing: '0px', leadingTrim: 'none' }}>VISIONARY</div>
        <div style={{ position: 'absolute', bottom: '20px', left: '10px', color: '#000', fontFamily: 'Helvetica', fontWeight: 700, fontStyle: 'normal', fontSize: '10px', lineHeight: '14px', letterSpacing: '0px', leadingTrim: 'none' }}>
          {typeof leadershipScores.visionary === 'number' ? leadershipScores.visionary.toFixed(2) : '0.00'}%
        </div>

        <div style={{ position: 'absolute', bottom: '5px', right: '10px', color: '#000', fontFamily: 'Helvetica', fontWeight: 700, fontStyle: 'normal', fontSize: '10px', lineHeight: '14px', letterSpacing: '0px', leadingTrim: 'none' }}>COLLABORATIVE</div>
        <div style={{ position: 'absolute', bottom: '20px', right: '10px', color: '#000', fontFamily: 'Helvetica', fontWeight: 700, fontStyle: 'normal', fontSize: '10px', lineHeight: '14px', letterSpacing: '0px', leadingTrim: 'none' }}>
          {typeof leadershipScores.collaborative === 'number' ? leadershipScores.collaborative.toFixed(2) : '0.00'}%
        </div>


        <svg width={diagramSize} height={diagramSize} style={{ position: 'absolute', top: 0, left: 0 }}>
          <circle cx={dotPosition.x} cy={dotPosition.y} r="8" fill={arrowColor} />
          <path
            d={arrowPath}
            stroke={arrowColor}
            strokeWidth="4"
            strokeOpacity="1"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
          {arrowHeadPoints && (
            <polygon points={arrowHeadPoints} fill={arrowColor} stroke={arrowColor} />
          )}
        </svg>

      </div>
    );
  };

  const SquareWithArrow = () => {

    const topLeft = 'MIND';
    const topRight = 'STRENGTH';
    const bottomLeft = 'SOUL';
    const bottomRight = 'HEART';

    const topLeftValue = (scoreRankingOne.find(s => s.name === 'mind')?.value || 0).toFixed(2);
    const topRightValue = (scoreRankingOne.find(s => s.name === 'strength')?.value || 0).toFixed(2);
    const bottomLeftValue = (scoreRankingOne.find(s => s.name === 'soul')?.value || 0).toFixed(2);
    const bottomRightValue = (scoreRankingOne.find(s => s.name === 'heart')?.value || 0).toFixed(2);

    const labelStyle = { color: '#000', fontSize: '10px', fontWeight: '700', fontFamily: 'Helvetica, sans-serif', textTransform: 'uppercase', position: 'absolute', lineHeight: '1.2' };

    const percentageStyle = { color: '#000', fontSize: '10px', fontWeight: '700', fontFamily: 'Helvetica, sans-serif', position: 'absolute', lineHeight: '1.2' };


    const diagramSize = 206;
    const headLength = 14;
    const headWidth = 8;
    const centerOffsetX = diagramSize / 2;
    const centerOffsetY = diagramSize / 2;
    const quadrantOffsetX = centerOffsetX / 2;
    const quadrantOffsetY = centerOffsetY / 2;

    const quadrantCenters = {
      mind: { x: quadrantOffsetX, y: quadrantOffsetY },
      strength: { x: diagramSize - quadrantOffsetX, y: quadrantOffsetY },
      soul: { x: quadrantOffsetX, y: diagramSize - quadrantOffsetY },
      heart: { x: diagramSize - quadrantOffsetX, y: diagramSize - quadrantOffsetY }
    };

    // Get scores for each quadrant
    const quadrantScores = [
      { name: 'mind', score: scoreRankingOne.find(s => s.name === 'mind')?.value || 0 },
      { name: 'strength', score: scoreRankingOne.find(s => s.name === 'strength')?.value || 0 },
      { name: 'soul', score: scoreRankingOne.find(s => s.name === 'soul')?.value || 0 },
      { name: 'heart', score: scoreRankingOne.find(s => s.name === 'heart')?.value || 0 }
    ];

    const sortedQuadrants = applyFullTieOrder(
      [...quadrantScores].sort((a, b) => b.score - a.score),
      (q) => q.name,
      (q) => q.score
    )
      .map(({ name, score }) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value: score
      }));


    const pathPoints = sortedQuadrants.map(step => quadrantCenters[step.name.toLowerCase()]);



    let arrowPath = '';
    if (pathPoints.length > 0) {
      const pointsForPath = [...pathPoints];
      if (pointsForPath.length > 1) {
        const tip = pointsForPath[pointsForPath.length - 1];
        const prev = pointsForPath[pointsForPath.length - 2];
        const dx = tip.x - prev.x;
        const dy = tip.y - prev.y;
        const len = Math.hypot(dx, dy) || 1;
        const trimmedTip = {
          x: tip.x - (headLength * dx) / len,
          y: tip.y - (headLength * dy) / len
        };
        pointsForPath[pointsForPath.length - 1] = trimmedTip;
      }

      arrowPath = `M ${pointsForPath[0].x} ${pointsForPath[0].y}`;
      for (let i = 1; i < pointsForPath.length; i++) {
        arrowPath += ` L ${pointsForPath[i].x} ${pointsForPath[i].y}`;
      }
    }

    const dotPosition = sortedQuadrants[0] ?
      quadrantCenters[sortedQuadrants[0].name.toLowerCase()] :
      { x: 0, y: 0 };

    const arrowColor = '#B6111F';
    let arrowHeadPoints = '';



    if (sortedQuadrants.length > 1) {
      const lastIdx = sortedQuadrants?.length - 1;
      const tip = quadrantCenters[sortedQuadrants[lastIdx].name.toLowerCase()];
      const prev = quadrantCenters[sortedQuadrants[lastIdx - 1].name.toLowerCase()];
      const angle = Math.atan2(tip.y - prev.y, tip.x - prev.x);
      const left = {
        x: tip.x - headLength * Math.cos(angle) + headWidth * Math.sin(angle),
        y: tip.y - headLength * Math.sin(angle) - headWidth * Math.cos(angle)
      };
      const right = {
        x: tip.x - headLength * Math.cos(angle) - headWidth * Math.sin(angle),
        y: tip.y - headLength * Math.sin(angle) + headWidth * Math.cos(angle)
      };
      arrowHeadPoints = `${tip.x},${tip.y} ${left.x},${left.y} ${right.x},${right.y}`;
    }

    return (
      <div style={{ position: 'relative', width: '206px', height: '206px', backgroundColor: 'transparent', display: 'flex', justifyContent: 'center', alignItems: 'center', border: '2px solid #000' }}>

        <div style={{ ...labelStyle, top: '6px', left: '10px' }}>
          {topLeft}
        </div>
        <div style={{ ...labelStyle, top: '6px', right: '10px' }}>
          {topRight}
        </div>

        <div style={{ ...percentageStyle, top: '20px', left: '10px' }}>
          {topLeftValue}%
        </div>

        <div style={{ ...percentageStyle, top: '20px', right: '10px' }}>
          {topRightValue}%
        </div>

        <div style={{ ...percentageStyle, bottom: '23px', left: '10px' }}>
          {bottomLeftValue}%
        </div>
        <div style={{ ...labelStyle, bottom: '10px', left: '10px' }}>
          {bottomLeft}
        </div>

        <div style={{ ...percentageStyle, bottom: '23px', right: '10px' }}>
          {bottomRightValue}%
        </div>
        <div style={{ ...labelStyle, bottom: '10px', right: '10px' }}>
          {bottomRight}
        </div>

        {/* Vertical divider */}
        <div style={{ position: 'absolute', width: '2px', height: '100%', backgroundColor: '#000' }}></div>

        {/* Horizontal divider */}
        <div style={{ position: 'absolute', width: '100%', height: '2px', backgroundColor: '#000' }}></div>

        <svg width={diagramSize} height={diagramSize} style={{ position: 'absolute', top: 0, left: 0 }}>
          <circle cx={dotPosition.x} cy={dotPosition.y} r="8" fill={arrowColor} />
          <path
            d={arrowPath}
            stroke={arrowColor}
            strokeWidth="4"
            strokeOpacity="1"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
          {arrowHeadPoints && (
            <polygon points={arrowHeadPoints} fill={arrowColor} stroke={arrowColor} />
          )}
        </svg>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: "rgb(255, 255, 255)", }}>
      <HeaderPreview apiData={apiData} />
      <div className="page" style={{padding:"28px 0px"}}>
        <style>
          {`
            .container { display: flex; flex-direction: column; align-items: center; justify-content: flex-start; text-align: center; }
            .header-text { font-size: 22px; font-weight: 700; color: #426B88; margin-bottom: 31px; margin-top: 19px; }
            .chart { height: 290px; width: 371px; line-height: 41px; letter-spacing: 0; } 
            .footer { display: flex; justify-content: space-between; border-top: 1px solid #00577D; padding-top: 10px; margin-top: 20px; }
            .footerTextHeading { font-size: 12px; font-weight: 700; color: #76A3BE; }
            .footerText { font-size: 10px; color: #76A3BE; }
          `}
        </style>
        <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "16px", alignItems:"center" }}>
            <h2 style={{ fontSize: "11px", fontFamily: "Helvetica", fontWeight: "700", lineHeight: "15px", color:"#000000",margin:"0px"  }}>LIFE FLOW</h2>
            {SquareWithArrow()}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "16px", alignItems:"center"  }}>
            <h2 style={{ fontSize: "11px", fontFamily: "Helvetica", fontWeight: "700", lineHeight: "15px",  color:"#000000", margin:"0px"  }}>LEADERSHIP FLOW</h2>
            {SquareWithArrowTwo()}
          </div>
        </div>


        <div className="container">
          <SpiderChart
            snapshotResult={snapshotResult}
            starterLabel={starterLabel}
            leadershipResult={leadershipResult}
            leadershipLabel={leadershipLabel}
          />
        </div>
      </div>
      <FooterPreview pageNumber={pageNumber} />
    </div>
  );
};

export default AlignmentFlowSecond;

// max = max(heart_round1_m,soul_round1_m,mind_round1_m,strength_round1_m)
// most = 28

// max_2ndMost = max(heart_round2_m,soul_round2_m,mind_round2_m,strength_round2_m)
// 2nd_most = 30/2


// let most_score = Math.max(heart_r1_m, soul_r1_m, mind_r1_m, strength_r1_m)
// let second_most_score = Math.max(heart_r2_m, soul_r2_m, mind_r2_m, strength_r2_m) / 2
// let least_score = Math.max(heart_r1_l, soul_r1_l, mind_r1_l, strength_r1_l)
// let second_least_score = Math.max(heart_r2_l, soul_r2_l, mind_r2_l, strength_r2_l) / 2
